import type { HabitRepeat, ItemType, TimeConstraintType } from "./types";
import { BROWSER_STORAGE_KEYS } from "../browser-storage";

export type DailyGoalCreationDraft = {
  title: string;
  type: ItemType;
  timeFrom: string;
  timeTo: string;
  dateKey: string;
  weeklyGoalId: string;
  contributionAmount: number;
  contributionUnit: string;
  repeat: HabitRepeat;
  section: "scheduled" | "unscheduled";
  timeConstraintType: TimeConstraintType;
};

export type WeeklyGoalCreationDraft = {
  title: string;
  type: ItemType;
  timeFrom: string;
  timeTo: string;
  weekStartDate: string;
  weeklyGoalId: string;
  amount: string;
  unit: string;
  progress: string;
  category: string;
  showManualProgress?: boolean;
  repeat: HabitRepeat;
};

export type MonthlyGoalCreationDraft = {
  title: string;
  target: string;
  unit: string;
  progress: string;
  yearGoal: string;
  yearGoalManuallySelected?: boolean;
  showManualProgress?: boolean;
};

export type YearGoalCreationDraft = {
  title: string;
  globalGoal: string;
  target: string;
  unit: string;
  progress: string;
  showManualProgress?: boolean;
};

export type GlobalGoalCreationDraft = {
  title: string;
  target: string;
  unit: string;
  progress: string;
  showManualProgress?: boolean;
};

export type GoalCreationDrafts = {
  dailyByDate: Record<string, DailyGoalCreationDraft>;
  weeklyByStart: Record<string, WeeklyGoalCreationDraft>;
  monthlyByMonth: Record<string, MonthlyGoalCreationDraft>;
  yearlyByYear: Record<string, YearGoalCreationDraft>;
  global: GlobalGoalCreationDraft;
};

type StoredGoalCreationDrafts = {
  ownerEmail: string;
  drafts: GoalCreationDrafts;
};

const STORAGE_KEY = BROWSER_STORAGE_KEYS.goalCreationDrafts;
const dateKeyPattern = /^\d{4}-\d{2}-\d{2}$/;
const monthKeyPattern = /^\d{4}-\d{2}$/;
const yearKeyPattern = /^\d{4}$/;
const itemTypes = new Set<ItemType>(["goal", "habit"]);
const repeatCadences = new Set<HabitRepeat["cadence"]>(["none", "daily", "customDays", "weekly", "monthly"]);

export function emptyGoalCreationDrafts(): GoalCreationDrafts {
  return {
    dailyByDate: {},
    weeklyByStart: {},
    monthlyByMonth: {},
    yearlyByYear: {},
    global: { title: "", target: "", unit: "", progress: "" },
  };
}

function normalizeRepeat(value: unknown): HabitRepeat | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<HabitRepeat>;
  if (!record.cadence || !repeatCadences.has(record.cadence)) return null;
  const interval = Number(record.interval);
  if (!Number.isFinite(interval) || interval <= 0) return null;
  return {
    cadence: record.cadence,
    interval,
    excludedWeekdays: Array.isArray(record.excludedWeekdays)
      ? record.excludedWeekdays.filter((day): day is number => Number.isInteger(day) && day >= 0 && day <= 6)
      : [],
    excludedDates: Array.isArray(record.excludedDates)
      ? record.excludedDates.filter((date): date is string => typeof date === "string" && dateKeyPattern.test(date))
      : [],
  };
}

function normalizeDailyDraft(value: unknown, dateKey: string): DailyGoalCreationDraft | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<DailyGoalCreationDraft>;
  const repeat = normalizeRepeat(record.repeat);
  if (!itemTypes.has(record.type as ItemType) || !repeat || (record.section !== "scheduled" && record.section !== "unscheduled")) return null;
  const amount = Number(record.contributionAmount);
  return {
    title: typeof record.title === "string" ? record.title : "",
    type: record.type as ItemType,
    timeFrom: typeof record.timeFrom === "string" ? record.timeFrom : "",
    timeTo: typeof record.timeTo === "string" ? record.timeTo : "",
    dateKey,
    weeklyGoalId: typeof record.weeklyGoalId === "string" ? record.weeklyGoalId : "",
    contributionAmount: Number.isFinite(amount) && amount >= 0 ? amount : 0,
    contributionUnit: typeof record.contributionUnit === "string" ? record.contributionUnit : "",
    repeat,
    section: record.section,
    timeConstraintType: record.timeConstraintType === "WORK_WINDOW" ? "WORK_WINDOW" : "BLOCKING",
  };
}

function normalizeWeeklyDraft(value: unknown, weekStartDate: string): WeeklyGoalCreationDraft | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<WeeklyGoalCreationDraft>;
  const repeat = normalizeRepeat(record.repeat);
  if (!itemTypes.has(record.type as ItemType) || !repeat) return null;
  return {
    title: typeof record.title === "string" ? record.title : "",
    type: record.type as ItemType,
    timeFrom: typeof record.timeFrom === "string" ? record.timeFrom : "",
    timeTo: typeof record.timeTo === "string" ? record.timeTo : "",
    weekStartDate: typeof record.weekStartDate === "string" && dateKeyPattern.test(record.weekStartDate) ? record.weekStartDate : weekStartDate,
    weeklyGoalId: typeof record.weeklyGoalId === "string" ? record.weeklyGoalId : "",
    amount: typeof record.amount === "string" ? record.amount : "",
    unit: typeof record.unit === "string" ? record.unit : "",
    progress: typeof record.progress === "string" ? record.progress : "",
    category: typeof record.category === "string" ? record.category : "",
    showManualProgress: record.showManualProgress === true || undefined,
    repeat,
  };
}

function normalizeRecord<T>(value: unknown, keyPattern: RegExp, normalize: (entry: unknown, key: string) => T | null): Record<string, T> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce<Record<string, T>>((acc, [key, entry]) => {
    if (!keyPattern.test(key)) return acc;
    const normalized = normalize(entry, key);
    if (normalized) acc[key] = normalized;
    return acc;
  }, {});
}

function normalizeMonthlyDraft(value: unknown): MonthlyGoalCreationDraft | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<MonthlyGoalCreationDraft>;
  return {
    title: typeof record.title === "string" ? record.title : "",
    target: typeof record.target === "string" ? record.target : "",
    unit: typeof record.unit === "string" ? record.unit : "",
    progress: typeof record.progress === "string" ? record.progress : "",
    yearGoal: typeof record.yearGoal === "string" ? record.yearGoal : "",
    yearGoalManuallySelected: record.yearGoalManuallySelected === true || undefined,
    showManualProgress: record.showManualProgress === true || undefined,
  };
}

function normalizeYearDraft(value: unknown): YearGoalCreationDraft | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<YearGoalCreationDraft>;
  return {
    title: typeof record.title === "string" ? record.title : "",
    globalGoal: typeof record.globalGoal === "string" ? record.globalGoal : "",
    target: typeof record.target === "string" ? record.target : "",
    unit: typeof record.unit === "string" ? record.unit : "",
    progress: typeof record.progress === "string" ? record.progress : "",
    showManualProgress: record.showManualProgress === true || undefined,
  };
}

function normalizeGlobalDraft(value: unknown, legacyTitle: unknown): GlobalGoalCreationDraft {
  if (!value || typeof value !== "object") {
    return {
      title: typeof legacyTitle === "string" ? legacyTitle : "",
      target: "",
      unit: "",
      progress: "",
    };
  }
  const record = value as Partial<GlobalGoalCreationDraft>;
  return {
    title: typeof record.title === "string" ? record.title : "",
    target: typeof record.target === "string" ? record.target : "",
    unit: typeof record.unit === "string" ? record.unit : "",
    progress: typeof record.progress === "string" ? record.progress : "",
    showManualProgress: record.showManualProgress === true || undefined,
  };
}

function normalizeDrafts(value: unknown): GoalCreationDrafts {
  if (!value || typeof value !== "object") return emptyGoalCreationDrafts();
  const record = value as Partial<GoalCreationDrafts>;
  return {
    dailyByDate: normalizeRecord(record.dailyByDate, dateKeyPattern, normalizeDailyDraft),
    weeklyByStart: normalizeRecord(record.weeklyByStart, dateKeyPattern, normalizeWeeklyDraft),
    monthlyByMonth: normalizeRecord(record.monthlyByMonth, monthKeyPattern, (entry) => normalizeMonthlyDraft(entry)),
    yearlyByYear: normalizeRecord(record.yearlyByYear, yearKeyPattern, (entry) => normalizeYearDraft(entry)),
    global: normalizeGlobalDraft(
      record.global,
      (record as { globalTitle?: unknown }).globalTitle
    ),
  };
}

export function goalDraftOwnerEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function loadGoalCreationDrafts(ownerEmail: string): GoalCreationDrafts {
  const normalizedOwner = goalDraftOwnerEmail(ownerEmail);
  if (!normalizedOwner) return emptyGoalCreationDrafts();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyGoalCreationDrafts();
    const stored = JSON.parse(raw) as Partial<StoredGoalCreationDrafts>;
    if (goalDraftOwnerEmail(stored.ownerEmail ?? "") !== normalizedOwner) return emptyGoalCreationDrafts();
    return normalizeDrafts(stored.drafts);
  } catch {
    return emptyGoalCreationDrafts();
  }
}

export function saveGoalCreationDrafts(ownerEmail: string, drafts: GoalCreationDrafts): void {
  const normalizedOwner = goalDraftOwnerEmail(ownerEmail);
  if (!normalizedOwner) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ownerEmail: normalizedOwner, drafts } satisfies StoredGoalCreationDrafts));
  } catch {
    // Draft retention is a convenience; storage errors must not block goal entry.
  }
}
